import { useState, useCallback } from 'react';
import { mercadoPagoApi } from '../api/mercadopago';
import { MercadoPagoOAuthStartResponse, MercadoPagoOAuthErrorResponse } from '../api/types/mercadopago';

// Global reference to prevent duplicate OAuth start calls
let globalOAuthStartingRef: { current: boolean } = { current: false };

/**
 * Hook for managing MercadoPago OAuth flow
 */
export const useMercadoPagoOAuthFlow = () => {
  const [isStarting, setIsStarting] = useState(false);
  const [startError, setStartError] = useState<string | null>(null);
  const [alreadyConnected, setAlreadyConnected] = useState<MercadoPagoOAuthErrorResponse | null>(null);

  /**
   * Start OAuth flow and redirect to MercadoPago
   */
  const startOAuthFlow = useCallback(async () => {
    // Prevent duplicate OAuth start calls globally
    if (globalOAuthStartingRef.current) {
      console.log('🔄 Flujo OAuth ya está iniciándose globalmente, ignorando...');
      return;
    }

    // Prevent duplicate calls locally
    if (isStarting) {
      console.log('🔄 Flujo OAuth ya está iniciándose en este componente, ignorando...');
      return;
    }

    globalOAuthStartingRef.current = true;
    setIsStarting(true);
    setStartError(null);
    setAlreadyConnected(null);

    try {
      console.log('🚀 Iniciando flujo OAuth de MercadoPago...');
      
      const response: MercadoPagoOAuthStartResponse = await mercadoPagoApi.startOAuth();

      console.log('✅ URL de autorización obtenida:', response.authorization_url);
      console.log('🔐 State:', response.state);
      console.log('⏰ Expira:', response.expires_at);
      console.log('📝 Instrucciones:', response.instructions);

      // Store state in localStorage for validation later (persists across tabs/windows)
      localStorage.setItem('mercadopago_oauth_state', response.state);
      console.log('💾 Estado OAuth guardado en localStorage:', response.state);
      
      // Redirect to MercadoPago authorization page
      window.location.href = response.authorization_url;
      
    } catch (err: any) {
      console.error('❌ Error al iniciar OAuth de MercadoPago:', err);
      
      // Handle 409 - Company already has active credentials
      if (err.response?.status === 409) {
        const errorData: MercadoPagoOAuthErrorResponse = err.response.data;
        console.log('⚠️ La empresa ya tiene credenciales activas:', errorData);
        setAlreadyConnected(errorData);
        setStartError(`Ya tienes credenciales activas que expiran el ${new Date(errorData.expires_at).toLocaleDateString()}`);
      } else {
        setStartError(err.response?.data?.error || err.message || 'Error iniciando OAuth de MercadoPago');
      }
    } finally {
      setIsStarting(false);
      globalOAuthStartingRef.current = false;
    }
  }, [isStarting]);

  /**
   * Get the callback URL for this domain
   */
  const getCallbackUrl = useCallback(() => {
    const baseUrl = window.location.origin;
    return `${baseUrl}/configuracion?mercadopago_callback=true`;
  }, []);

  /**
   * Start OAuth with automatic callback URL
   */
  const startOAuthWithCallback = useCallback(async () => {
    const callbackUrl = getCallbackUrl();
    console.log('🔗 Callback URL configurado en el frontend:', callbackUrl);
    console.log('ℹ️ El backend manejará la URL de callback automáticamente');
    await startOAuthFlow();
  }, [startOAuthFlow, getCallbackUrl]);

  return {
    isStarting,
    startError,
    alreadyConnected,
    startOAuthFlow,
    startOAuthWithCallback,
    getCallbackUrl,
  };
};
